// Customers (CRM): every family across Preschool and Holiday Club, matched into one record each, with lifecycle stage,
// timeline and lifetime value, a review queue for possible duplicates, insights, and the old customer map.
//
// All numbers are computed in the browser from the existing collections (shared/crm.ts); only the manager's
// decisions are saved. `npm run dev` serves in-memory mock data (shared/crmStub.ts) and skips the manager check.

import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { AppContext } from "../Store";
import { useSnackbar } from "../shared/SnackbarProvider";
import { StatStrip } from "../shared/StatStrip";
import {
  DEFAULT_ASSUMPTIONS,
  STAGE_LABEL,
  buildCrm,
  canonicalPhone,
  fold,
  newCustomerId,
  saveCrmChanges,
  todayIso,
  watchCrmInput,
  type CrmCustomer,
  type CrmInput,
  type Stage,
} from "../shared/crm";
import { createActions, type Planned } from "./crm/actions";
import { CustomerCard } from "./crm/CustomerCard";
import { DuplicateCard } from "./crm/DuplicateCard";
import { EnquiryDialog } from "./crm/EnquiryDialog";
import { InsightsPanel } from "./crm/InsightsPanel";
import { money } from "./crm/ui";

type TabId = "customers" | "duplicates" | "insights" | "map";
type SortId = "ltv" | "realised" | "predicted" | "recent" | "name";
const STAGE_FILTERS: Stage[] = ["active", "registered", "holidayOnly", "enquiry", "lapsed", "alumni"];
const PAGE = 40;

const SORTS: Record<SortId, { label: string; compare: (a: CrmCustomer, b: CrmCustomer) => number }> = {
  ltv: { label: "Lifetime value", compare: (a, b) => b.ltvPence - a.ltvPence },
  realised: { label: "Realised", compare: (a, b) => b.realised.invoicedPence - a.realised.invoicedPence },
  predicted: {
    label: "Predicted",
    compare: (a, b) => (b.prediction.preschoolPence + b.prediction.holidayPence) - (a.prediction.preschoolPence + a.prediction.holidayPence),
  },
  recent: { label: "Last activity", compare: (a, b) => (b.lastActivity ?? "").localeCompare(a.lastActivity ?? "") },
  name: { label: "Name", compare: (a, b) => a.name.localeCompare(b.name) },
};

const matches = (customer: CrmCustomer, search: string) => {
  const q = fold(search.trim());
  if (!q) return true;
  const digits = canonicalPhone(search) || (search.replace(/\D/g, "").length >= 5 ? search.replace(/\D/g, "") : "");
  const haystack = [
    customer.name, ...customer.emails, ...customer.addresses, ...customer.children.map((c) => c.name),
    ...customer.identities.flatMap((i) => [...i.parentNames, i.childName]),
  ].map(fold).join(" | ");
  return haystack.includes(q) || (!!digits && customer.phones.some((p) => p.replace(/\D/g, "").includes(digits.replace(/^0/, ""))));
};

const CustomerMap: React.FC = () => (
  <Paper variant="outlined" sx={{ overflow: "hidden" }}>
    <iframe
      title="Customer map"
      width="100%"
      height="750"
      src="https://lookerstudio.google.com/embed/reporting/7e39ca39-740b-49c8-a2df-df192f88afdc/page/k7WyE"
      frameBorder={0}
      style={{ border: 0, display: "block" }}
      allowFullScreen
      sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
    />
  </Paper>
);

const CustomerPage: React.FC = () => {
  const { userContext } = useContext(AppContext);
  const snack = useSnackbar();
  const allowed = import.meta.env.DEV || !!userContext?.isAdmin();

  const [input, setInput] = useState<CrmInput | null>(null);
  const [loadErrors, setLoadErrors] = useState<string[]>([]);
  const [tab, setTab] = useState<TabId>("customers");
  const [filter, setFilter] = useState<Stage | "all">("all");
  const [bothOnly, setBothOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortId>("ltv");
  const [openId, setOpenId] = useState<string | null>(null);
  const [limit, setLimit] = useState(PAGE);
  const [busy, setBusy] = useState(false);
  const [undo, setUndo] = useState<{ message: string; revert: () => Promise<void> } | null>(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const today = todayIso();

  useEffect(() => {
    if (!allowed) return undefined;
    return watchCrmInput(setInput, (source, error) => {
      setLoadErrors((errors) => (errors.includes(source) ? errors : [...errors, source]));
      console.warn(`CRM: could not read ${source}`, error);
    });
  }, [allowed]);

  const result = useMemo(() => (input ? buildCrm(input, today, DEFAULT_ASSUMPTIONS) : null), [input, today]);
  const docs = useMemo(() => new Map((input?.customers ?? []).map((d) => [d.id, d])), [input]);
  const actions = useMemo(() => createActions(docs, newCustomerId), [docs]);
  const byId = useMemo(() => new Map((result?.customers ?? []).map((c) => [c.id, c])), [result]);

  if (!allowed) {
    return <Box sx={{ padding: 2 }}><Alert severity="warning">Only the manager can see customers.</Alert></Box>;
  }

  const run = async (planned: Planned, message: string) => {
    setBusy(true);
    try {
      await saveCrmChanges(planned.apply);
      setUndo({ message, revert: () => saveCrmChanges(planned.revert) });
    } catch (error) {
      snack(`Could not save: ${error instanceof Error ? error.message : String(error)}`, "error");
    } finally {
      setBusy(false);
    }
  };

  const customers = result?.customers ?? [];
  const suggestions = (result?.suggestions ?? []).filter((s) => byId.has(s.a) && byId.has(s.b));
  const stageCount = (stage: Stage) => customers.filter((c) => c.stage === stage && (!bothOnly || c.services.length === 2)).length;
  const shown = customers
    .filter((c) => (filter === "all" || c.stage === filter) && (!bothOnly || c.services.length === 2) && matches(c, search))
    .sort((a, b) => SORTS[sort].compare(a, b) || a.name.localeCompare(b.name));
  const stats = result?.stats;
  const active = stats?.activeCustomers ?? 0;

  return (
    <Box sx={{ px: { xs: 1.5, md: 3 }, py: 2, maxWidth: 1280, mx: "auto" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={600}>Customers</Typography>
          <Typography variant="body2" color="text.secondary">Families across Preschool and Holiday Club</Typography>
        </Box>
        <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => setEnquiryOpen(true)}>Add enquiry</Button>
      </Stack>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tab value="customers" label="Customers" />
        <Tab value="duplicates" label={
          <Badge color="warning" badgeContent={suggestions.length} sx={{ "& .MuiBadge-badge": { right: -12 } }}>Possible duplicates</Badge>
        } />
        <Tab value="insights" label="Insights" />
        <Tab value="map" label="Map" />
      </Tabs>

      {loadErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not read {loadErrors.join(", ")}; figures leave them out. (Are the Firestore rules for the CRM deployed?)
        </Alert>
      )}

      {tab === "map" && <CustomerMap />}
      {tab !== "map" && !result && <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box>}

      {tab === "customers" && result && stats && (
        <>
          <StatStrip items={[
            { label: "Families", color: "primary", value: String(stats.customers), hint: `${active} current · ${stats.bothServices} use both services` },
            { label: "Realised", color: "success", value: money(stats.invoicedPence),
              hint: stats.invoicedPence ? `${Math.round((stats.paidPence / stats.invoicedPence) * 100)}% paid · ${money(stats.invoicedPence - stats.paidPence)} due` : undefined,
              progress: stats.invoicedPence ? (stats.paidPence / stats.invoicedPence) * 100 : 0 },
            { label: "Predicted future", color: "secondary", value: money(stats.predictedPence),
              hint: active ? `≈ ${money(Math.round(stats.predictedPence / active))} per current family` : undefined },
            { label: "Possible duplicates", color: suggestions.length ? "warning" : "success", value: String(suggestions.length),
              hint: suggestions.length ? `${suggestions.filter((s) => s.confidence === "high").length} high confidence · review` : "All clear",
              onClick: () => setTab("duplicates") },
          ]} />

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
            <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, value) => value && setFilter(value)} sx={{ flexWrap: "wrap" }}>
              <ToggleButton value="all">All {customers.filter((c) => !bothOnly || c.services.length === 2).length}</ToggleButton>
              {STAGE_FILTERS.filter((s) => stageCount(s) > 0 || s === filter).map((s) => (
                <ToggleButton key={s} value={s}>{STAGE_LABEL[s]} {stageCount(s)}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Chip label={`Both services ${stats.bothServices}`} color={bothOnly ? "primary" : "default"} variant={bothOnly ? "filled" : "outlined"}
              onClick={() => setBothOnly(!bothOnly)} />
            <Box sx={{ flexGrow: 1 }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sort</InputLabel>
              <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value as SortId)}>
                {(Object.keys(SORTS) as SortId[]).map((id) => <MenuItem key={id} value={id}>{SORTS[id].label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" placeholder="Search name, child, email, phone" value={search}
              onChange={(e) => { setSearch(e.target.value); setLimit(PAGE); }} sx={{ minWidth: 260 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          </Stack>

          <Stack spacing={1}>
            {shown.slice(0, limit).map((customer) => (
              <CustomerCard key={customer.id} customer={customer} open={openId === customer.id} today={today}
                onToggle={() => setOpenId(openId === customer.id ? null : customer.id)}
                identities={result.identities} actions={actions} run={run} />
            ))}
            {shown.length > limit && (
              <Button onClick={() => setLimit(limit + PAGE)}>Show more ({shown.length - limit})</Button>
            )}
            {shown.length === 0 && (
              <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">{customers.length === 0 ? "No customers yet." : "Nothing matches."}</Typography>
              </Paper>
            )}
          </Stack>
        </>
      )}

      {tab === "duplicates" && result && (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Records with the same email are linked automatically. These look like the same family under a different email, phone or
            spelling. Merging links them for good; "Not the same" is remembered, so the pair will not come back.
          </Typography>
          {suggestions.map((s) => (
            <DuplicateCard key={s.id} suggestion={s} a={byId.get(s.a)!} b={byId.get(s.b)!} busy={busy}
              onMerge={() => run(actions.merge(byId.get(s.a)!, byId.get(s.b)!), `Merged ${byId.get(s.a)!.name} and ${byId.get(s.b)!.name}`)}
              onReject={() => run(actions.reject(s), "Marked as different families")} />
          ))}
          {suggestions.length === 0 && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">No possible duplicates. Nice and tidy.</Typography>
            </Paper>
          )}
        </Stack>
      )}

      {tab === "insights" && result && (
        <InsightsPanel result={result} assumptions={DEFAULT_ASSUMPTIONS} onStage={(stage) => {
          setFilter(stage as Stage);
          setTab("customers");
        }} />
      )}

      <EnquiryDialog open={enquiryOpen} today={today} onClose={() => setEnquiryOpen(false)} onSave={(enquiry) => {
        setEnquiryOpen(false);
        run(actions.createEnquiry(enquiry), `Enquiry from ${enquiry.parentName} added`);
      }} />

      <Snackbar
        open={!!undo}
        autoHideDuration={6000}
        onClose={(_, reason) => reason !== "clickaway" && setUndo(null)}
        message={undo?.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        action={
          <Button color="inherit" size="small" sx={{ fontWeight: 700 }} onClick={() => {
            const revert = undo!.revert;
            setUndo(null);
            revert().catch((error) => snack(`Could not undo: ${error.message}`, "error"));
          }}>
            Undo
          </Button>
        }
      />
    </Box>
  );
};

export default CustomerPage;
